import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEstimatedProgress } from "@/hooks/useEstimatedProgress";

const SHIMMER_DURATION_MS = 2500;
const GLOW_DURATION_MS = 3500;

function syncDelay(durationMs: number): number {
  const elapsed = typeof performance !== "undefined" ? performance.now() : 0;
  return -(elapsed % durationMs);
}

interface PreviewSkeletonTileProps {
  /** When generation started, used to drive the asymptotic % curve. */
  startTime: string;
  /** Tailwind aspect ratio class — each flow's preview pair has its own
   *  framing (Baseball Trend = 4/5, Talking Avatar = 3/4). */
  aspectClass: string;
}

/** Rich placeholder for a single in-flight preview tile.
 *  Mirrors the "progress bar + label" pattern from PendingProgressCard
 *  (used for video generation) but sized to fit inside an existing tile
 *  in a preview-pair grid — no outer border, no own aspect ratio. */
export function PreviewSkeletonTile({
  startTime,
  aspectClass,
}: PreviewSkeletonTileProps) {
  const { t } = useLanguage();
  const { progress } = useEstimatedProgress({ startTime, mediaType: "image" });

  const syncDelays = useMemo(
    () => ({
      shimmer: syncDelay(SHIMMER_DURATION_MS),
      glow: syncDelay(GLOW_DURATION_MS),
    }),
    [],
  );

  return (
    <div
      className={cn(
        "relative rounded-2xl overflow-hidden border border-border bg-gradient-to-br from-muted to-secondary flex flex-col items-center justify-center gap-2",
        aspectClass,
      )}
    >
      <div
        className="absolute inset-0 animate-shimmer-glow"
        style={{ animationDelay: `${syncDelays.glow}ms` }}
      />
      <div
        className="absolute inset-0 animate-shimmer"
        style={{ animationDelay: `${syncDelays.shimmer}ms` }}
      />

      <div className="relative flex flex-col items-center gap-1.5 w-full px-4">
        <div className="w-full max-w-[120px] h-1.5 bg-border/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${progress}%`,
              boxShadow: "0 0 8px hsl(var(--brand) / 0.4)",
            }}
          />
        </div>

        <span className="text-muted-foreground text-[10px] md:text-xs font-medium tabular-nums">
          {progress}%
        </span>

        <span className="text-muted-foreground text-xs md:text-sm font-medium text-center">
          {t.generate.previewInProgress}
        </span>
      </div>
    </div>
  );
}
